import { useEffect, useState } from "react";
import { ChipSizeEnum } from "../../../components/core/Chip/Chip";
import { CollaboratorChip } from "../../../components/core/CollaboratorChip/CollaboratorChip";
import { Loader } from "../../../components/core/Loader/Loader";
import SearchInput from "../../../components/core/SearchInput/SearchInput";
import { MatrixPerformanceGrid } from "../../../components/MatrixPerformanceGrid/MatrixPerformanceGrid";
import { MessageBox } from "../../../components/MessageBox/MessageBox";
import { BodyTyp, TitlesTyp } from "../../../components/Typography/Typography";
import {
  COLLABORATORS_LABEL,
  EMPTY_COLLABORATORS_MESSAGE,
  EMPTY_STRING,
  NAME_SURNAME_EMAIL_LABEL,
  RESULTS_LABEL,
} from "../../../constants/Dictionary";
import { MatrixSizes } from "../../../constants/PotentialMatrixConstants";
import { PotentialMatrixFilters } from "../../../hooks/usePotentialMatrixFilters.hook";
import { MatrixPerformanceItem } from "../../../types/api/potentialMatrix.type";
import {
  CollaboratorInfo,
  FontWeightEnum,
  IconSizeEnum,
} from "../../../types/AppTypes";
import {
  StyledDetailsHeader,
  StyledMatrixDetails,
  StyledMatrixDetailsContent,
} from "../DevelopmentMatrixDetails/DevelopmentMatrixDetails.styled";
import { StyledMessageContainer } from "../PotentialMatrixV3.styled";
import { useDebounce } from "use-debounce";
import { MATRIX_COLLABORATORS_PAGE_SIZE } from "../../../constants/AppConstants";
import { IconManager } from "../../../components/core/IconManager/IconManager";
import { ReactComponent as CloseIcon } from "../../../icons/close.svg";
import { Pagination } from "../../../components/core/Pagination/Pagination";
import { usePerformanceMatrixDetails } from "../../../hooks/potentialMatrix/usePotentialMatrixPerfomance.hook";
import { useAppSelector } from "../../../hooks/store.hook";
import { getInternalGradeMetaFromLabel } from "../../../utils/potentialMatrixGrade.utils";

type PerformanceMatrixDetailsProps = {
  item: MatrixPerformanceItem;
  onClose: () => void;
  filters: PotentialMatrixFilters;
  yearSelected: number;
  onClickResume: (collaborator: CollaboratorInfo) => void;
  matrixData: MatrixPerformanceItem[];
  isAnimatingClose?: boolean;
};

export const PerformanceMatrixDetails = ({
  item,
  onClose,
  filters,
  yearSelected,
  onClickResume,
  matrixData,
  isAnimatingClose,
}: PerformanceMatrixDetailsProps) => {
  const { config } = useAppSelector((state) => state.config);
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [pageNumber, setPageNumber] = useState(0);

  const { data, isFetching } = usePerformanceMatrixDetails({
    enabled: !!item,
    searchDebouncedValue,
    item,
    pageNumber,
    filters,
  });

  const formatImpactLabel = (label?: string) =>
    label
      ? label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()
      : EMPTY_STRING;

  const getText = () => {
    const potentialMatrixConfig = config.find((c) => c.year === yearSelected)
      ?.config?.finalInterviewDimensions;
    const configItem = potentialMatrixConfig?.find(
      (c) => c.id === item.dimensionId,
    );

    if (!configItem) return "";

    const impactLabel = formatImpactLabel(item.impact);

    const rangeLabel =
      configItem.range.length === 2
        ? `${configItem.range[0]}-${configItem.range[1]}%`
        : EMPTY_STRING;

    return `${COLLABORATORS_LABEL} in ${impactLabel} | ${configItem.initials} ${rangeLabel}`;
  };

  useEffect(() => {
    setPageNumber(0);
  }, [searchDebouncedValue, filters]);

  return (
    <StyledMatrixDetailsContent
      $visible={!!item}
      $isAnimatingClose={isAnimatingClose}
    >
      <div className="first-column">
        <div className="matrix-container">
          <MatrixPerformanceGrid
            data={matrixData}
            size={MatrixSizes.SMALL}
            yearSelected={yearSelected}
            {...(item && { selectedItem: item })}
          />
        </div>
      </div>
      <StyledMatrixDetails>
        <StyledDetailsHeader>
          <TitlesTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
            {getText()}
          </TitlesTyp>
          <IconManager onClick={onClose} size={IconSizeEnum.MEDIUM}>
            <CloseIcon />
          </IconManager>
        </StyledDetailsHeader>
        {item ? (
          <>
            <StyledDetailsHeader>
              <BodyTyp fontWeight={FontWeightEnum.MEDIUM} variant="2">
                {isFetching ? "" : `${item.count ?? 0} ${RESULTS_LABEL}`}
              </BodyTyp>
              <div className="search-container">
                <SearchInput
                  value={searchValue}
                  onChange={setSearchValue}
                  label={NAME_SURNAME_EMAIL_LABEL}
                  name={"search"}
                />
              </div>
            </StyledDetailsHeader>
            <Loader loading={isFetching} className="profiles-container">
              {data?.data.length === 0 ? (
                <StyledMessageContainer>
                  <MessageBox message={EMPTY_COLLABORATORS_MESSAGE} />
                </StyledMessageContainer>
              ) : (
                <>
                  <div className="profiles-grid">
                    {data?.data.map((collaboratorCalibration) => {
                      const collaboratorGradeMeta =
                        getInternalGradeMetaFromLabel(
                          collaboratorCalibration.collaborator.internalGrade,
                        );

                      return (
                        <CollaboratorChip
                          key={collaboratorCalibration.collaborator.id}
                          collaborator={collaboratorCalibration.collaborator}
                          size={ChipSizeEnum.BIG}
                          onClick={onClickResume}
                          {...(collaboratorGradeMeta?.color && {
                            borderColor: collaboratorGradeMeta.color,
                          })}
                        />
                      );
                    })}
                  </div>
                </>
              )}
            </Loader>
            <Pagination
              current={pageNumber + 1}
              total={data?.pagination.total ?? 0}
              onChange={(pageNumber) => setPageNumber(pageNumber - 1)}
              pageSize={MATRIX_COLLABORATORS_PAGE_SIZE}
            />
          </>
        ) : null}
      </StyledMatrixDetails>
    </StyledMatrixDetailsContent>
  );
};
